import BN from "bn.js";
import Decimal from "decimal.js";
import { Curve } from "@raydium-io/raydium-sdk-v2";
import { IDX_LAUNCHLAB_PLATFORM_SETTINGS } from "@/config/idxLaunchlabLandingConfig";
import {
  IDX_LAUNCHLAB_ON_CHAIN_CURVE,
  IDX_LAUNCHLAB_ON_CHAIN_FEE_RATES,
} from "@/lib/idx-launchlab/platform-on-chain-config";
import {
  LAUNCHLAB_DEFAULT_SUPPLY,
  LAUNCHLAB_DEFAULT_TOTAL_FUND_RAISING_B,
  LAUNCHLAB_DEFAULT_TOTAL_SELL_A,
} from "@/lib/idx-launchlab/build-create-launch-args";

/** Max SOL on bonding curve before graduation (JustSendit preset). */
export const LAUNCHLAB_GRADUATION_TARGET_SOL =
  IDX_LAUNCHLAB_ON_CHAIN_CURVE.graduationTargetSol;

/** @param {string} raw */
export function clampInitialBuySolInput(raw) {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed) return "";
  const n = Number.parseFloat(trimmed);
  if (!Number.isFinite(n) || n <= 0) return trimmed;
  if (n > LAUNCHLAB_GRADUATION_TARGET_SOL) {
    return String(LAUNCHLAB_GRADUATION_TARGET_SOL);
  }
  return trimmed;
}

/** @param {number} initialBuySol */
export function effectiveInitialBuySol(initialBuySol) {
  if (!Number.isFinite(initialBuySol) || initialBuySol <= 0) return 0;
  return Math.min(initialBuySol, LAUNCHLAB_GRADUATION_TARGET_SOL);
}

/** @param {BN} quoteLamportsInVault */
function graduationProgressFromQuote(quoteLamportsInVault) {
  const pct = new Decimal(quoteLamportsInVault.toString())
    .div(LAUNCHLAB_DEFAULT_TOTAL_FUND_RAISING_B.toString())
    .mul(100);
  return Math.min(100, Math.max(0, pct.toNumber()));
}


/** Mainnet SOL GlobalConfig `tradeFeeRate` (per 1_000_000) for 6s1xP3hp… */
const LAUNCHLAB_PROTOCOL_TRADE_FEE_RATE = new BN(2500);

const CURVE_TYPE = IDX_LAUNCHLAB_PLATFORM_SETTINGS.curveType;
const DECIMALS_A = IDX_LAUNCHLAB_PLATFORM_SETTINGS.decimals;
const DECIMALS_B = 9;
const MIGRATE_FEE = new BN(0);
const TOTAL_LOCKED = new BN(IDX_LAUNCHLAB_ON_CHAIN_CURVE.totalLockedAmount);

/** @returns {{ virtualA: BN; virtualB: BN; totalSellA: BN; totalFundRaisingB: BN }} */
function getLaunchCurveInit() {
  const initParam = Curve.getCurve(CURVE_TYPE).getInitParam({
    supply: LAUNCHLAB_DEFAULT_SUPPLY,
    totalFundRaising: LAUNCHLAB_DEFAULT_TOTAL_FUND_RAISING_B,
    totalSell: LAUNCHLAB_DEFAULT_TOTAL_SELL_A,
    totalLockedAmount: TOTAL_LOCKED,
    migrateFee: MIGRATE_FEE,
  });

  return {
    virtualA: initParam.a,
    virtualB: initParam.b,
    totalSellA: LAUNCHLAB_DEFAULT_TOTAL_SELL_A,
    totalFundRaisingB: LAUNCHLAB_DEFAULT_TOTAL_FUND_RAISING_B,
  };
}

/**
 * @param {Decimal} price — SOL per whole token (not lamports)
 */
export function formatLaunchlabSpotPriceSol(price) {
  if (!price || !price.isFinite() || price.lte(0)) return "—";
  const n = price.toNumber();
  if (n >= 1) return `${price.toFixed(4)} SOL`;
  if (n >= 0.0001) return `${price.toFixed(8)} SOL`;
  return `${price.toSignificantDigits(4)} SOL`;
}

/**
 * Preview bonding-curve spot price at launch (0 SOL) or after optional first buy.
 * @param {number} [initialBuySol]
 */
export function previewInitialBuyQuote(initialBuySol = 0) {
  const effectiveSol = effectiveInitialBuySol(initialBuySol);
  const { virtualA, virtualB, totalSellA, totalFundRaisingB } = getLaunchCurveInit();

  if (effectiveSol <= 0) {
    const launchPrice = Curve.getPoolInitPriceByInit({
      a: virtualA,
      b: virtualB,
      decimalA: DECIMALS_A,
      decimalB: DECIMALS_B,
      curveType: CURVE_TYPE,
    });
    const marketCapSol = launchPrice.mul(IDX_LAUNCHLAB_PLATFORM_SETTINGS.supplyHuman);
    return {
      postBuyPriceSol: launchPrice,
      tokensReceived: new Decimal(0),
      marketCapSol,
      isLaunchPrice: true,
      graduationProgressPercent: 0,
    };
  }

  const amountBLamports = new BN(Math.round(effectiveSol * 1e9));
  if (amountBLamports.lte(new BN(0))) return null;

  const poolInfo = {
    virtualA,
    virtualB,
    realA: new BN(0),
    realB: new BN(0),
    totalSellA,
    totalFundRaisingB,
  };

  const buy = Curve.buyExactIn({
    poolInfo,
    amountB: amountBLamports,
    protocolFeeRate: LAUNCHLAB_PROTOCOL_TRADE_FEE_RATE,
    platformFeeRate: new BN(IDX_LAUNCHLAB_ON_CHAIN_FEE_RATES.platformRatePerMillion),
    curveType: CURVE_TYPE,
    shareFeeRate: new BN(0),
    creatorFeeRate: new BN(IDX_LAUNCHLAB_ON_CHAIN_FEE_RATES.creatorRatePerMillion),
    transferFeeConfigA: undefined,
    slot: 0,
  });

  const realA = buy.amountA.amount.sub(buy.amountA.fee ?? new BN(0));
  const realB = buy.amountB;
  const postBuyPrice = Curve.getPrice({
    poolInfo: { virtualA, virtualB, realA, realB },
    decimalA: DECIMALS_A,
    decimalB: DECIMALS_B,
    curveType: CURVE_TYPE,
  });

  const tokensReceived = new Decimal(realA.toString()).div(new Decimal(10).pow(DECIMALS_A));
  const marketCapSol = postBuyPrice.mul(IDX_LAUNCHLAB_PLATFORM_SETTINGS.supplyHuman);

  return {
    postBuyPriceSol: postBuyPrice,
    tokensReceived,
    marketCapSol,
    isLaunchPrice: false,
    graduationProgressPercent: graduationProgressFromQuote(realB),
  };
}

/**
 * Estimated launch market cap in USD (bonding-curve math × SOL spot).
 * @param {number} [initialBuySol]
 * @param {number | null | undefined} solUsdSpot
 * @returns {number | null}
 */
export function previewLaunchMarketCapUsd(initialBuySol = 0, solUsdSpot) {
  const spot = Number(solUsdSpot);
  if (!Number.isFinite(spot) || spot <= 0) return null;
  try {
    const preview = previewInitialBuyQuote(initialBuySol);
    const mcSol = preview.marketCapSol.toNumber();
    if (!Number.isFinite(mcSol) || mcSol <= 0) return null;
    return mcSol * spot;
  } catch {
    return null;
  }
}
